import React from 'react';

interface FilterBarProps {
  onFilterChange: (filter: string) => void;
  onSortChange:   (sort: string)   => void;
  currentFilter:  string;
  currentSort:    string;
  counts: { all: number; catalyst: number; momentum: number; rebound: number; none: number };
}

const FILTER_DEFINITIONS = [
  {
    label: 'All', value: 'all', countKey: 'all' as const,
    activeClass: 'text-violet-700 dark:text-violet-300',
    activeBadge: 'bg-violet-100 dark:bg-violet-400/20 text-violet-600 dark:text-violet-300',
    dot: null,
  },
  {
    label: 'Catalyst', value: 'catalyst', countKey: 'catalyst' as const,
    activeClass: 'text-blue-700 dark:text-[#4f8ff8]',
    activeBadge: 'bg-[#4f8ff8]/15 text-[#4f8ff8]',
    dot: 'bg-[#4f8ff8]',
  },
  {
    label: 'Momentum', value: 'momentum', countKey: 'momentum' as const,
    activeClass: 'text-amber-700 dark:text-amber-400',
    activeBadge: 'bg-amber-100 dark:bg-amber-400/20 text-amber-600 dark:text-amber-400',
    dot: 'bg-amber-400',
  },
  {
    label: 'Rebound', value: 'rebound', countKey: 'rebound' as const,
    activeClass: 'text-emerald-700 dark:text-[#10d98e]',
    activeBadge: 'bg-emerald-100 dark:bg-[#10d98e]/20 text-emerald-600 dark:text-[#10d98e]',
    dot: 'bg-[#10d98e]',
  },
  {
    label: 'Unconfirmed', value: 'none', countKey: 'none' as const,
    activeClass: 'text-gray-700 dark:text-[#d5deea]',
    activeBadge: 'bg-gray-200 dark:bg-[#1d2d47] text-gray-600 dark:text-[#c2d0e0]',
    dot: null,
  },
];

const SORT_DEFINITIONS = [
  { label: '% Change',    value: 'change'   },
  { label: 'Rel Volume',  value: 'volume'   },
  { label: 'Detected',    value: 'detected' },
];

// Renders the filter tabs (all/catalyst/momentum/none) and sort buttons for the scanner table
export function FilterBar({ onFilterChange, onSortChange, currentFilter, currentSort, counts }: FilterBarProps) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-6">

      {/* ── Filter tabs ── */}
      <div className="overflow-x-auto scrollbar-none -mx-1 px-1">
      <div className="flex items-center gap-1 bg-gray-100/80 dark:bg-[#0e1624] border border-gray-200 dark:border-[#1d2d47] rounded-xl p-1 w-max">
        {FILTER_DEFINITIONS.map((f) => {
          const isActive = currentFilter === f.value;
          const count    = counts[f.countKey];
          return (
            <button
              key={f.value}
              onClick={() => onFilterChange(f.value)}
              className={`
                relative flex items-center gap-2 px-3.5 py-1.5 rounded-lg
                text-[12px] font-medium transition-all duration-200 whitespace-nowrap
                ${isActive
                  ? `bg-white dark:bg-[#141e30] shadow-sm dark:shadow-[0_1px_3px_rgba(0,0,0,0.5)] ${f.activeClass}`
                  : 'text-gray-500 dark:text-[#c2d0e0] hover:text-gray-800 dark:hover:text-[#d5deea] hover:bg-white/60 dark:hover:bg-[#141e30]/60'
                }
              `}
            >
              {/* Colored dot for non-all filters */}
              {f.dot && isActive && (
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${f.dot}`} />
              )}
              <span>{f.label}</span>
              <span className={`
                inline-flex items-center justify-center
                min-w-[18px] h-[18px] px-1 rounded-md
                text-[9.5px] font-mono font-semibold tabular-nums
                transition-colors duration-200
                ${isActive
                  ? f.activeBadge
                  : 'bg-gray-200/70 dark:bg-[#1d2d47] text-gray-400 dark:text-[#a3b6cc]'
                }
              `}>
                {count}
              </span>
            </button>
          );
        })}
      </div>
      </div>

      {/* ── Divider ── */}
      <div className="hidden sm:block w-px h-5 bg-gray-200 dark:bg-[#1d2d47] mx-1" />

      {/* ── Sort group ── */}
      <div className="flex items-center gap-2">
        <span className="text-[10.5px] font-semibold tracking-[0.1em] text-gray-400 dark:text-[#a3b6cc] uppercase">
          Sort
        </span>

        <div className="flex items-center gap-1 bg-gray-100/80 dark:bg-[#0e1624] border border-gray-200 dark:border-[#1d2d47] rounded-xl p-1">
          {SORT_DEFINITIONS.map((s) => {
            const isActive = currentSort === s.value;
            return (
              <button
                key={s.value}
                onClick={() => onSortChange(s.value)}
                className={`
                  px-3 py-1.5 rounded-lg text-[12px] font-medium
                  transition-all duration-200 whitespace-nowrap
                  ${isActive
                    ? 'bg-white dark:bg-[#141e30] text-gray-900 dark:text-[#f1f5fb] shadow-sm dark:shadow-[0_1px_3px_rgba(0,0,0,0.5)]'
                    : 'text-gray-500 dark:text-[#c2d0e0] hover:text-gray-800 dark:hover:text-[#d5deea]'
                  }
                `}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}